/**
 * intervento.js — Helper puri di presentazione per le tipologie di intervento.
 *
 * Il "codice" è l'identificativo macchina della tipologia
 * (es. `sfalcio_erba`), spesso restituito dalle API con alias `intervento`.
 * Il "nome" è il testo leggibile destinato all'utente (es. "Sfalcio Erba").
 *
 * Queste funzioni sono PURAMENTE di presentazione: non modificano valori
 * interni, payload API o dati salvati. Servono a mostrare il nome leggibile
 * (o un fallback formattato del codice) nei punti di sola lettura e nei
 * fallback dei menu a tendina, mentre il codice resta il valore interno.
 *
 * Esportate con `module.exports` per compatibilità con l'ambiente Jest
 * CommonJS usato dalle suite esistenti in `GUI/roadme-gui/tests`
 * (`require('../src/utils/intervento')`). Lo stesso modulo è importabile con
 * la sintassi ES (`import { nomeIntervento } from '@/utils/intervento'`) nei
 * componenti Vue grazie alla trasformazione di Babel.
 */

/**
 * formatCodiceIntervento — trasforma un codice macchina in testo leggibile.
 *
 * Regole:
 *   - Se `codice` è vuoto/nullo/undefined → ritorna stringa vuota "".
 *   - Altrimenti sostituisce gli underscore con spazi e applica l'iniziale
 *     maiuscola a ciascuna parola (es. `sfalcio_erba` → "Sfalcio Erba").
 *
 * @param {string} codice codice macchina della tipologia (es. "sfalcio_erba")
 * @returns {string} testo leggibile formattato, o "" se il codice è vuoto/nullo
 */
function formatCodiceIntervento(codice) {
  if (codice === null || codice === undefined) return "";
  const str = String(codice);
  if (str.trim() === "") return "";

  return str
    .split("_")
    .filter((parola) => parola.length > 0)
    .map((parola) => parola.charAt(0).toUpperCase() + parola.slice(1).toLowerCase())
    .join(" ");
}

/**
 * nomeIntervento — risolve un codice nel nome leggibile della tipologia.
 *
 * Regole:
 *   - Cerca in `lista` l'elemento con `i.codice === codice`; se trovato e dotato
 *     di un `nome` valido (presente e diverso dalla stringa vuota), ritorna
 *     `i.nome`.
 *   - Altrimenti ritorna `formatCodiceIntervento(codice)`.
 *   - `lista` è opzionale: se assente/vuota, si comporta come
 *     `formatCodiceIntervento`.
 *
 * @param {string} codice codice macchina della tipologia (es. "sfalcio_erba")
 * @param {Array<{codice: string, nome?: string}>} [lista] catalogo tipologie
 * @returns {string} nome leggibile dal catalogo, o fallback formattato del codice
 */
function nomeIntervento(codice, lista) {
  if (Array.isArray(lista) && lista.length > 0) {
    const trovato = lista.find((i) => i && i.codice === codice);
    // `nome` è considerato valido se presente e non è la stringa vuota.
    // Non applichiamo trim: qualsiasi nome fornito dal catalogo (anche con soli
    // spazi) è un valore ufficiale e va restituito as-is, coerentemente con le
    // Correctness Properties (Property 1: se il nome è presente si mostra il nome).
    if (
      trovato &&
      trovato.nome !== null &&
      trovato.nome !== undefined &&
      trovato.nome !== ""
    ) {
      return trovato.nome;
    }
  }
  return formatCodiceIntervento(codice);
}

module.exports = {
  formatCodiceIntervento,
  nomeIntervento
};
